import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNotifications } from '../contexts/NotificationContext';
import { useTutorial } from '../contexts/TutorialContext';
import VersionInfo from './VersionInfo';
import Characters from './Characters';
import HexWorldMap from './HexWorldMap';
import BattleGrid from './BattleGrid';
import Crafting from './Crafting';
import Achievements from './Achievements';
import AdminDashboard from './AdminDashboard';
import HelpOverlay from './HelpOverlay';
import TutorialLibrary from './TutorialLibrary';
import QuestProgressPanel from './QuestProgressPanel';

type ActiveScreen = 'home' | 'characters' | 'hexworld' | 'crafting' | 'achievements' | 'admin' | 'battle';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.herosofthegrid.simwebtech.com';

const Home: React.FC = () => {
  const { user, logout } = useAuth();
  const { addNotification } = useNotifications();
  const { startTutorial } = useTutorial();
  const [activeScreen, setActiveScreen] = useState<ActiveScreen>('home');
  const [currentBattleId, setCurrentBattleId] = useState<number | null>(null);
  const [isCreatingStarter, setIsCreatingStarter] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [showTutorialLibrary, setShowTutorialLibrary] = useState(false);

  // Helper function to handle navigation with battle warning
  const handleNavigation = (newScreen: ActiveScreen) => {
    // If currently in battle and trying to navigate away, show warning
    if (activeScreen === 'battle' && newScreen !== 'battle' && currentBattleId) {
      const confirmed = window.confirm(
        "You're currently in battle! Leaving now will abandon the fight.\n\nAre you sure you want to leave?"
      );
      if (!confirmed) {
        return; // Don't navigate
      }
    }
    setActiveScreen(newScreen);
  };

  // Helper function to handle logout with battle warning
  const handleLogout = () => {
    // If currently in battle, show warning
    if (activeScreen === 'battle' && currentBattleId) {
      const confirmed = window.confirm(
        "You're currently in battle! Logging out will abandon the fight.\n\nAre you sure you want to logout?"
      );
      if (!confirmed) {
        return; // Don't logout
      }
    }
    logout();
  };

  useEffect(() => {
    const checkAndCreateStarterCharacter = async () => {
      if (!user) return;

      const token = localStorage.getItem('token');
      if (!token) return;

      try {
        // Check if user has any characters
        const response = await fetch(`${API_URL}/api/characters`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const characters = await response.json();

          // If no characters, create a starter character
          if (characters.length === 0) {
            setIsCreatingStarter(true);

            const createResponse = await fetch(`${API_URL}/api/characters/create-starter`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              }
            });

            if (createResponse.ok) {
              setShowWelcome(true);
              setTimeout(() => setShowWelcome(false), 8000);

              // Show notification toast
              addNotification({
                type: 'info',
                title: 'Welcome to Heroes of the Grid!',
                message: 'Your first hero has been created. Begin your adventure!'
              });
            }

            setIsCreatingStarter(false);
          }
        }
      } catch (error) {
        console.error('Error checking/creating starter character:', error);
        setIsCreatingStarter(false);
      }
    };

    checkAndCreateStarterCharacter();
  }, [user]);


  const renderContent = () => {
    switch (activeScreen) {
      case 'characters':
        return <Characters />;
      case 'hexworld':
        return <HexWorldMap onStartBattle={(battleId) => {
          setCurrentBattleId(battleId);
          setActiveScreen('battle');
        }} />;
      case 'battle':
        return currentBattleId ? (
          <BattleGrid 
            battleId={currentBattleId} 
            onExitBattle={() => {
              setCurrentBattleId(null);
              setActiveScreen('hexworld');
            }} 
          />
        ) : (
          <div className="battle-error">
            <p>No battle ID available. Please return to world map and start a new battle.</p>
            <button onClick={() => setActiveScreen('hexworld')}>Return to World Map</button>
          </div>
        );
      case 'crafting':
        return <Crafting />;
      case 'achievements':
        return <Achievements />;
      case 'admin':
        return <AdminDashboard />;
      default:
        return (
          <div className="home-dashboard">
            {showWelcome && (
              <div className="welcome-banner">
                <h2>Welcome to Heroes of the Grid!</h2>
                <p>We've created your first hero to get you started. Visit the <strong>Characters</strong> page to view your hero, then explore the <strong>World Map</strong> to begin your adventure!</p>
              </div>
            )}
            {isCreatingStarter && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <p>Creating your first hero...</p>
              </div>
            )}
            <div className="welcome-section">
              <h2>Welcome back, {user?.username}!</h2>
              <p>Your turn-based tactical adventure awaits. Choose your path:</p>
              
              <div className="quick-actions">
                <div className="action-card action-card-characters" onClick={() => handleNavigation('characters')}>
                  <div className="card-icon">⚔️</div>
                  <h3>Characters</h3>
                  <p>Manage your heroes and unlock their potential</p>
                  <div className="card-hint">Click to view →</div>
                </div>
                <div className="action-card action-card-hexworld" onClick={() => handleNavigation('hexworld')}>
                  <div className="card-icon">🗺️</div>
                  <h3>World Map</h3>
                  <p>Explore a vast procedural world with resources and battles</p>
                  <div className="card-hint">Explore world →</div>
                </div>
                <div className="action-card action-card-crafting" onClick={() => handleNavigation('crafting')}>
                  <div className="card-icon">🔨</div>
                  <h3>Crafting</h3>
                  <p>Forge powerful equipment from gathered materials</p>
                  <div className="card-hint">Create items →</div>
                </div>
                <div className="action-card action-card-achievements" onClick={() => handleNavigation('achievements')}>
                  <div className="card-icon">🏆</div>
                  <h3>Achievements</h3>
                  <p>Track your progress and unlock rewards</p>
                  <div className="card-hint">View achievements →</div>
                </div>
              </div>

              <QuestProgressPanel />
            </div>

            <div className="recent-activity">
              <h3>Recent Activity</h3>
              <div className="activity-list">
                <div className="activity-item">
                  <span className="activity-icon">🎮</span>
                  <div className="activity-text">
                    <p>Battle system improvements deployed</p>
                    <small>AI turn processing and animations fixed</small>
                  </div>
                </div>
                <div className="activity-item">
                  <span className="activity-icon">⚡</span>
                  <div className="activity-text">
                    <p>Performance optimizations applied</p>
                    <small>ESLint warnings resolved</small>
                  </div>
                </div>
                <div className="activity-item">
                  <span className="activity-icon">🏠</span>
                  <div className="activity-text">
                    <p>Home dashboard enhanced</p>
                    <small>Phase 2 Step 7 completed</small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="home">
      {showHelp && <HelpOverlay onClose={() => setShowHelp(false)} />}
      {showTutorialLibrary && <TutorialLibrary onClose={() => setShowTutorialLibrary(false)} />}
      <header className="home-header">
        <h1>Heroes of the Grid</h1>
        <div className="user-info">
          <button onClick={() => setShowTutorialLibrary(true)} className="tutorial-test-button" title="Tutorial Library">
            📚
          </button>
          <button onClick={() => setShowHelp(true)} className="help-button" title="How to Play">
            ?
          </button>
          <span>Welcome, {user?.username}!</span>
          <button onClick={handleLogout} className="logout-button">
            Logout
          </button>
        </div>
      </header>
      
      <nav className="main-nav">
        <button
          className={`nav-button ${activeScreen === 'home' ? 'active' : ''}`}
          onClick={() => handleNavigation('home')}
        >
          Home
        </button>
        <button
          className={`nav-button ${activeScreen === 'characters' ? 'active' : ''}`}
          onClick={() => handleNavigation('characters')}
        >
          Characters
        </button>
        <button
          className={`nav-button ${activeScreen === 'hexworld' ? 'active' : ''}`}
          onClick={() => handleNavigation('hexworld')}
        >
          World Map
        </button>
        <button
          className={`nav-button ${activeScreen === 'crafting' ? 'active' : ''}`}
          onClick={() => handleNavigation('crafting')}
        >
          Crafting
        </button>
        <button
          className={`nav-button ${activeScreen === 'achievements' ? 'active' : ''}`}
          onClick={() => handleNavigation('achievements')}
        >
          Achievements
        </button>
        {user?.role === 'Admin' && (
          <button
            className={`nav-button ${activeScreen === 'admin' ? 'active' : ''}`}
            onClick={() => handleNavigation('admin')}
          >
            Admin Panel
          </button>
        )}
      </nav>
      
      <main className="main-content">
        {renderContent()}
      </main>
      
      <footer className="version-footer">
        <VersionInfo />
      </footer>
    </div>
  );
};

export default Home;